import _ from 'lodash';
import type { BlocksAppData, Preset } from '@wix/document-services-types';
import type {
  WidgetPreset,
  WidgetPresetsManifest,
} from '@wix/editor-platform-sdk-types';
import { DEFAULT_WIDGET_WIDTH, DEFAULT_WIDGET_HEIGHT } from '../constants';
import type {
  PresetEditorPresence,
  CommonImage,
} from '@wix/ambassador-devcenter-v1-component/types';
import type { IBobWidgetImages } from '../services/BobWidgetServices/IBobWidgetImages';

export const MAX_PRESET_PANEL_THUMBNAIL_WIDTH = 240;

const getPresetEditorPresence = (
  presetId: string,
  editorPresenceByPresetId: Record<string, PresetEditorPresence>,
): PresetEditorPresence | undefined => {
  return editorPresenceByPresetId[presetId.replace('#', '')];
};

const isPresetVisible = (
  editorPresenceByPresetId: Record<string, PresetEditorPresence>,
) => {
  return (preset: Preset): boolean => {
    const presetEditorPresence = getPresetEditorPresence(
      preset.presetId,
      editorPresenceByPresetId,
    );
    return !presetEditorPresence || !!presetEditorPresence.showInPresetsPanel;
  };
};
const isCommonImage = (
  thumbnail: CommonImage | Preset['defaultSize'],
): thumbnail is CommonImage => {
  return thumbnail ? 'url' in thumbnail : false;
};
const getPreferredThumbnailSize = (
  thumbnailOrPresetSize: CommonImage | Preset['defaultSize'],
): { width: number; height: number } => {
  if (isCommonImage(thumbnailOrPresetSize)) {
    return {
      width: thumbnailOrPresetSize.width!,
      height: thumbnailOrPresetSize.height!,
    };
  }

  const width = thumbnailOrPresetSize?.width?.value || DEFAULT_WIDGET_WIDTH;
  const height = thumbnailOrPresetSize?.height?.value || DEFAULT_WIDGET_HEIGHT;

  return { width, height };
};

const getCustomThumbnail = (
  presetId: string,
  editorPresenceByPresetId: Record<string, PresetEditorPresence>,
): CommonImage | undefined => {
  const presetEditorPresence = getPresetEditorPresence(
    presetId,
    editorPresenceByPresetId,
  );
  return presetEditorPresence?.wixMediaThumbnail;
};

const fitDimensionsToMaxWidth = ({
  width,
  height,
}: {
  width: number;
  height: number;
}) => {
  if (width <= MAX_PRESET_PANEL_THUMBNAIL_WIDTH) {
    return { width, height };
  }

  const imageRatio = height / width;
  const adjustedWidth = Math.min(width, MAX_PRESET_PANEL_THUMBNAIL_WIDTH);
  return {
    width: adjustedWidth,
    height: _.floor(imageRatio * adjustedWidth),
  };
};

export const getWidgetDesignPresets = (
  appData: BlocksAppData,
  widgetId: string,
  presets: Preset[] | undefined,
  presetsEditorPresence: PresetEditorPresence[],
  bobWidgetImages: IBobWidgetImages,
): WidgetPreset[] => {
  const editorPresenceByPresetId = _.keyBy(presetsEditorPresence, 'presetId');

  return (presets || [])
    .filter(isPresetVisible(editorPresenceByPresetId))
    .map(({ presetId, defaultSize, name }) => {
      const maybeCustomThumbnail = getCustomThumbnail(
        presetId,
        editorPresenceByPresetId,
      );
      const presetEditorPresence = getPresetEditorPresence(
        presetId,
        editorPresenceByPresetId,
      );

      const preferredSize = getPreferredThumbnailSize(
        maybeCustomThumbnail || defaultSize,
      );

      const { width, height } = fitDimensionsToMaxWidth(preferredSize);

      return bobWidgetImages.getWidgetPresetsThumbnails(
        appData,
        widgetId,
        presetId,
        name,
        width,
        height,
        presetEditorPresence,
      );
    });
};

export const getWidgetPresetsManifest = (
  appData: BlocksAppData,
  widgetId: string,
  presets: Preset[],
  bobWidgetImages: IBobWidgetImages,
  presetsEditorPresence?: PresetEditorPresence[],
): { widgetPresets?: WidgetPresetsManifest } => {
  const widgetPresets = getWidgetDesignPresets(
    appData,
    widgetId,
    presets,
    presetsEditorPresence || [],
    bobWidgetImages,
  );

  return widgetPresets && widgetPresets.length > 1
    ? {
        widgetPresets: {
          isDesignButtonVisible: true,
          isLayoutButtonVisible: true,
          helpId: '4d57ebec-28dd-4d5a-ab38-1acd7503de14',
          presets: widgetPresets,
        },
      }
    : {};
};
